import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Target, Radar, Users, CalendarClock, Library, Trophy,
  ArrowRight, ShieldCheck, MessageSquare, Sparkles,
} from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import { API_URL } from '../config';
import '../styles/ScamSmart.css';

const API = `${API_URL}/api`;
const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const ScamSmartDashboard = ({ ssEmail }) => {
  const [scenarios, setScenarios] = useState([]);
  const [latest, setLatest] = useState(null); // last test result from localStorage

  useEffect(() => {
    axios.get(`${API}/scamsmart/scenarios/all`, { params: { email: ssEmail } })
      .then(({ data }) => setScenarios(data.scenarios || []))
      .catch(() => {});
    try {
      const raw = localStorage.getItem('ss_latest_result');
      if (raw) setLatest(JSON.parse(raw));
    } catch {}
  }, [ssEmail]);

  // Pick deterministic Weekly Drill — rotate by ISO week number
  const weekly = useMemo(() => {
    if (!scenarios.length) return null;
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const week = Math.floor(((now - start) / 86400000 + start.getDay() + 1) / 7);
    return scenarios[week % scenarios.length];
  }, [scenarios]);

  return (
    <div className="ss-page" data-testid="ss-dashboard">
      <header className="ss-header">
        <div className="ss-header-inner">
          <Link to="/" className="ss-brand">
            <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
            <div>
              <div className="ss-brand-text">All Secured</div>
              <div className="ss-brand-sub">ScamSmart</div>
            </div>
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <ScamIQBadge />
            <Link to="/scamsmart" className="ss-header-cta">Home</Link>
          </div>
        </div>
      </header>

      <section className="ss-dash-hero">
        <div className="ss-dash-hero-inner">
          <div className="ss-dash-eyebrow">Welcome back</div>
          <h1 className="ss-dash-title">Welcome to ScamSmart</h1>
          <p className="ss-dash-sub">
            Practise realistic scam scenarios, learn your family's weak spots, and build safer
            pause-and-verify habits — one short session at a time.
          </p>

          {latest && (
            <div className="ss-report" style={{ marginTop: 26, padding: '22px 24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 22, flexWrap: 'wrap' }}>
                <div>
                  <div className="ss-hero-iq">Your Scam IQ</div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <span className="ss-report-iq" style={{ fontSize: 56 }}>{latest.score}</span>
                    <span className="ss-preview-iq-suffix">/ 100</span>
                  </div>
                  <div className="ss-report-rank">{latest.rank}</div>
                </div>
                <Link to="/scamsmart/test" className="ss-btn-ghost" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)', marginLeft: 'auto' }}>
                  Re-take the test <ArrowRight size={16} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="ss-section">
        <div className="ss-section-eyebrow">Your training tools</div>
        <h2 className="ss-section-title" style={{ fontSize: 24 }}>Pick where to start</h2>

        <div className="ss-tile-grid">
          <Link to="/scamsmart/test" className="ss-tile" data-testid="ss-tile-test">
            <div className="ss-tile-icon"><Target size={22} /></div>
            <h3 className="ss-tile-title">{latest ? 'Continue Scam IQ Test' : 'Take the full Scam IQ Test'}</h3>
            <p className="ss-tile-desc">10 realistic scenarios, your full Scam IQ score, strongest category, weakest category, and a recommended next training pack.</p>
            <span className="ss-tile-arrow">Start <ArrowRight size={14} /></span>
          </Link>

          <Link to="/scamsmart/radar" className="ss-tile" data-testid="ss-tile-radar">
            <div className="ss-tile-icon"><Radar size={22} /></div>
            <h3 className="ss-tile-title">Scam Radar</h3>
            <p className="ss-tile-desc">Paste a suspicious message before you click, reply, pay, or share a code. Get a calm risk read in 5 seconds.</p>
            <span className="ss-tile-arrow">Open <ArrowRight size={14} /></span>
          </Link>

          <Link to="/scamsmart/practice" className="ss-tile ss-tile-featured" data-testid="ss-tile-practice">
            <div className="ss-tile-badge">New</div>
            <div className="ss-tile-icon"><Sparkles size={22} /></div>
            <h3 className="ss-tile-title">AI Practice Mode</h3>
            <p className="ss-tile-desc">Live chat against an AI scammer playing one of 6 Australian scam personas. Calm, safe, but realistic — like a flight simulator for scams.</p>
            <span className="ss-tile-arrow">Start practising <ArrowRight size={14} /></span>
          </Link>

          <Link to="/scamsmart/safe-word" className="ss-tile" data-testid="ss-tile-safeword">
            <div className="ss-tile-icon"><Users size={22} /></div>
            <h3 className="ss-tile-title">Family Safe Word</h3>
            <p className="ss-tile-desc">Set up your family's anti-scam verification phrase in under 60 seconds, then print a card for the fridge.</p>
            <span className="ss-tile-arrow">Set up <ArrowRight size={14} /></span>
          </Link>

          <div className="ss-tile ss-tile-disabled" data-testid="ss-tile-library" style={{ display: 'none' }} />
          <Link to="/scamsmart/library" className="ss-tile" data-testid="ss-tile-library">
            <div className="ss-tile-icon"><Library size={22} /></div>
            <h3 className="ss-tile-title">Scenario Library</h3>
            <p className="ss-tile-desc">Browse the premium scenario library by category and difficulty — train your weak spot or test the kids.</p>
            <span className="ss-tile-arrow">Browse <ArrowRight size={14} /></span>
          </Link>

          <Link to="/scamsmart/family" className="ss-tile" data-testid="ss-tile-family">
            <div className="ss-tile-icon"><Trophy size={22} /></div>
            <h3 className="ss-tile-title">Family Scorecard</h3>
            <p className="ss-tile-desc">Track everyone's Scam IQ, strongest topic, and weak spots — without lecturing them.</p>
            <span className="ss-tile-arrow">Open <ArrowRight size={14} /></span>
          </Link>

          <Link to="/scamsmart/trusted" className="ss-tile" data-testid="ss-tile-trusted">
            <div className="ss-tile-icon"><ShieldCheck size={22} /></div>
            <h3 className="ss-tile-title">Trusted guidance</h3>
            <p className="ss-tile-desc">Source-basis explanations, last-reviewed dates, and what to do if you've already been scammed.</p>
            <span className="ss-tile-arrow">Open <ArrowRight size={14} /></span>
          </Link>
        </div>

        {weekly && (
          <Link to="/scamsmart/test" className="ss-weekly" style={{ textDecoration: 'none' }} data-testid="ss-weekly">
            <div>
              <p className="ss-weekly-tag"><CalendarClock size={12} style={{ verticalAlign: '-2px', marginRight: 6 }} /> Weekly Scam Drill</p>
              <h3 className="ss-weekly-title">{weekly.title}</h3>
              <p className="ss-weekly-meta">
                <MessageSquare size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
                {weekly.messageType.toUpperCase()} &middot; ~60 seconds &middot; {weekly.difficulty}
              </p>
            </div>
            <button className="ss-btn-primary" style={{ whiteSpace: 'nowrap' }}>
              Start drill <ArrowRight size={16} />
            </button>
          </Link>
        )}

        <p className="ss-disclaimer">
          ScamSmart is an awareness and decision-training tool. It cannot detect every scam or guarantee protection.
          Always verify important requests through official or trusted channels.
        </p>
      </section>
    </div>
  );
};

export default ScamSmartDashboard;
